import {
  Box,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TextFieldComponent from "components/input/TextFieldComponent";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent, { DataHeaderType } from "components/table/TableRowComponent";
import { HEADER_TIRE_BRAND } from "constants/data";
import { getUserType } from "util/auth";
import { useMemo, useState } from "react";
import { restrictDecimalInput } from "utils";
import { BrandDetailTireEdit, BrandDetailTireUpdateRequest } from "../types/tire";

interface Props {
  tireBrandList: BrandDetailTireEdit[];
  onChangeItem?: (index: number, key: string, value: any) => void;
  onUpdateItem?: (index: number, key: keyof BrandDetailTireUpdateRequest) => void;
  setErrors: (data: any) => void;
}

/**
 * The TableTireBrand component.
 * @param {object} props - The component props.
 */
export default function TableTireBrand({
  tireBrandList,
  onChangeItem,
  onUpdateItem,
  setErrors,
}: Props) {
  const userType = getUserType();
  const [error, setError] = useState<Record<string, boolean>>({});

  // Modify header labels to add ※1 and ※2 with vertical centering
  const modifiedHeaders = useMemo(
    () =>
      HEADER_TIRE_BRAND.map((header): DataHeaderType => {
        // Check if label contains "A表" (handles both "A表売価掛率" and "A表\n売価掛け率")
        if (typeof header.label === "string" && header.label.includes("A表")) {
          return {
            ...header,
            label: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  lineHeight: "normal",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                }}
              >
                {header.label}
                <Box component="span" sx={{ ml: 1, fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  ※1
                </Box>
              </Box>
            ),
          };
        }
        // Check if label is "粗利率（％）"
        if (header.label === "粗利率（％）") {
          return {
            ...header,
            label: (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  lineHeight: "normal",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                }}
              >
                {header.label}
                <Box component="span" sx={{ ml: 0.5, fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  ※2
                </Box>
              </Box>
            ),
          };
        }
        return header;
      }),
    [],
  );

  const onChangeFloatNumberCell = (index: number, key: string, value: string) => {
    const newValue = restrictDecimalInput(value, 3);
    if (onChangeItem) onChangeItem(index, key, newValue);
  };

  return (
    <TableContainer
      component={Table}
      sx={{
        width: "100%",
        minWidth: 1200,
        borderCollapse: "unset",
        borderSpacing: "2px 1px",
      }}
    >
      <TableHead
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: "var(--offwhite-400)",
        }}
      >
        <TableRowComponent type="header" data={modifiedHeaders} />
      </TableHead>
      <TableBody>
        {tireBrandList.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <TableRow key={item.brand_cd + index}>
            <TableCellComponent
              sx={{
                border: "3px solid var(--white)",
                backgroundColor: "var(--offwhite-400)",
                p: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid var(--silver-150)",
                  height: "48px",
                  boxSizing: "border-box",
                }}
              >
                <Typography sx={{ color: "var(--black)", fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  {item.brand_cd}
                </Typography>
              </Box>
            </TableCellComponent>
            <TableCellComponent
              sx={{
                border: "3px solid var(--white)",
                p: 0,
                ...(userType === "C0002" && { backgroundColor: "var(--offwhite-400)" }),
              }}
            >
              {userType === "C0002" ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid var(--silver-150)",
                    height: "50px",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography sx={{ color: "var(--black)", fontSize: { xs: 16, md: 18, lg: 20 } }}>
                    {item.maker_name}
                  </Typography>
                </Box>
              ) : (
                <TextFieldComponent
                  sxNotchedOutline={{
                    border: "2px solid var(--silver-150)",
                    borderRadius: 0,
                  }}
                  value={item.maker_name}
                  sxInput={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                  }}
                  onChangeText={(value) => onChangeItem && onChangeItem(index, "maker_name", value)}
                  onBlur={() =>
                    onUpdateItem &&
                    !error[`maker_name-${index}`] &&
                    onUpdateItem(index, "maker_name")
                  }
                  validateOption={{
                    maxLength: {
                      value: 100,
                      message: "",
                    },
                  }}
                  onError={(err) => {
                    setError((prev) => ({
                      ...prev,
                      [`maker_name-${index}`]: err,
                    }));
                  }}
                />
              )}
            </TableCellComponent>
            <TableCellComponent
              sx={{
                border: "3px solid var(--white)",
                p: 0,
                ...(userType === "C0002" && { backgroundColor: "var(--offwhite-400)" }),
              }}
            >
              {userType === "C0002" ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid var(--silver-150)",
                    height: "48px",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography sx={{ color: "var(--black)", fontSize: { xs: 16, md: 18, lg: 20 } }}>
                    {item.maker_name}
                  </Typography>
                </Box>
              ) : (
                <TextFieldComponent
                  sxNotchedOutline={{
                    border: "2px solid var(--silver-150)",
                    borderRadius: 0,
                  }}
                  value={item.brand_name}
                  sxInput={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                  }}
                  onChangeText={(value) => onChangeItem && onChangeItem(index, "brand_name", value)}
                  onBlur={() =>
                    onUpdateItem &&
                    !error[`brand_name-${index}`] &&
                    onUpdateItem(index, "brand_name")
                  }
                  disabled={userType === "C0002"}
                  validateOption={{
                    maxLength: {
                      value: 100,
                      message: "",
                    },
                  }}
                  onError={(err) => {
                    setError((prev) => ({
                      ...prev,
                      [`brand_name-${index}`]: err,
                    }));
                  }}
                />
              )}
            </TableCellComponent>
            <TableCellComponent
              sx={{
                border: "3px solid var(--white)",
                ...(userType === "C0002" && { backgroundColor: "var(--offwhite-400)" }),
                p: 0,
              }}
            >
              <TextFieldComponent
                type="string"
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  textAlign: "center",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderRadius: 0,
                }}
                disabled={userType === "C0002"}
                value={item.item_category_m}
                onBlur={() =>
                  onUpdateItem &&
                  !error[`item_category_m_${index}`] &&
                  onUpdateItem(index, "item_category_m")
                }
                validateOption={{
                  maxLength: {
                    value: 10,
                    message: "",
                  },
                }}
                onInputText={(e) => {
                  onChangeItem?.(index, "item_category_m", e.target.value);
                }}
                onError={(isErr) => setError({ ...error, [`item_category_m_${index}`]: isErr })}
              />
            </TableCellComponent>
            <TableCellComponent sx={{ border: "3px solid var(--white)", p: 0 }}>
              <TextFieldComponent
                type="string"
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: item.sale_price_rate_atable_color,
                  textAlign: "center",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderRadius: 0,
                }}
                value={item?.sale_price_rate_atable}
                onBlur={() =>
                  onUpdateItem &&
                  !error[`sale_price_rate_atable_${index}`] &&
                  onUpdateItem(index, "sale_price_rate_atable")
                }
                validateOption={{
                  numericFormat: {
                    precision: 9,
                    scale: 2,
                  },
                }}
                onInputText={(e) =>
                  onChangeFloatNumberCell(index, "sale_price_rate_atable", e.target.value)
                }
                onError={(isErr) =>
                  setError({ ...error, [`sale_price_rate_atable_${index}`]: isErr })
                }
              />
            </TableCellComponent>
            <TableCellComponent sx={{ border: "3px solid var(--white)", p: 0 }}>
              <TextFieldComponent
                type="string"
                sxInput={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  color: item.fixed_discount_color,
                  textAlign: "center",
                }}
                sxNotchedOutline={{
                  border: "2px solid var(--silver-150)",
                  borderRadius: 0,
                }}
                value={item.fixed_discount}
                onBlur={() =>
                  onUpdateItem &&
                  !error[`fixed_discount_${index}`] &&
                  onUpdateItem(index, "fixed_discount")
                }
                validateOption={{
                  numericFormat: {
                    precision: 9,
                    scale: 0,
                  },
                }}
                onInputText={(e) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  onChangeItem &&
                    onChangeItem(index, "fixed_discount", e.target.value.replace(/[^0-9]/g, ""));
                }}
                onError={(isErr) => setError({ ...error, [`fixed_discount_${index}`]: isErr })}
              />
            </TableCellComponent>
            <TableCellComponent
              sx={{
                border: "3px solid var(--white)",
                p: 0,
              }}
            >
              {userType !== "C0001" ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid var(--silver-150)",
                    height: "50px",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography sx={{ color: "var(--black)", fontSize: { xs: 16, md: 18, lg: 20 } }}>
                    {item?.set_profit_ratio}
                  </Typography>
                </Box>
              ) : (
                <TextFieldComponent
                  type="string"
                  sxInput={{
                    fontSize: { xs: 16, md: 18, lg: 20 },
                    color: item.set_profit_ratio_color,
                    textAlign: "center",
                  }}
                  sxNotchedOutline={{
                    border: "2px solid var(--silver-150)",
                    borderRadius: 0,
                  }}
                  value={item?.set_profit_ratio}
                  onBlur={() =>
                    onUpdateItem &&
                    !error[`set_profit_ratio_${index}`] &&
                    onUpdateItem(index, "set_profit_ratio")
                  }
                  validateOption={{
                    numericFormat: {
                      precision: 9,
                      scale: 0,
                    },
                  }}
                  onInputText={(e) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                    onChangeItem &&
                      onChangeItem(
                        index,
                        "set_profit_ratio",
                        e.target.value.replace(/[^0-9]/g, ""),
                      );
                  }}
                  onError={(isErr) => setError({ ...error, [`set_profit_ratio_${index}`]: isErr })}
                />
              )}
            </TableCellComponent>
          </TableRow>
        ))}
      </TableBody>
    </TableContainer>
  );
}
